import type { OnDestroy, OnInit } from '@angular/core';
import { Directive, ElementRef, EventEmitter, Input, Output, inject } from '@angular/core';
import { DOCUMENT } from '@angular/common';
import { Subscription } from 'rxjs';
import { fromEvent } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

@Directive({
    selector: '[webHorizontalResizeHandle]',
    standalone: true,
})
export class HorizontalResizeHandlerDirective implements OnInit, OnDestroy {
    private readonly elementRef = inject(ElementRef);
    private readonly document = inject(DOCUMENT);

    @Input() public targetElement!: HTMLElement;
    @Input() public direction: 'left' | 'right' = 'right';
    @Input() public rotation = 0;
    @Input() public minWidth = 50;

    @Output() public widthChangeStart = new EventEmitter<void>();
    @Output() public widthChange = new EventEmitter<number>();
    @Output() public widthChangeEnd = new EventEmitter<void>();

    private startSub!: Subscription;
    private dragSub = new Subscription();

    private startX = 0;
    private startY = 0;
    private startWidth = 0;

    private rAF = 0;
    private pendingEvent: MouseEvent | null = null;

    public ngOnInit(): void {
        if (!(this.targetElement instanceof HTMLElement)) {
            console.warn('webHorizontalResizeHandle: targetElement is not set or invalid.');
            return;
        }

        const mousedown$ = fromEvent<MouseEvent>(this.elementRef.nativeElement, 'mousedown');
        const mousemove$ = fromEvent<MouseEvent>(this.document, 'mousemove');
        const mouseup$ = fromEvent<MouseEvent>(this.document, 'mouseup');

        this.startSub = mousedown$.subscribe((startEvent) => {
            startEvent.preventDefault();
            startEvent.stopPropagation();

            this.startX = startEvent.clientX;
            this.startY = startEvent.clientY;
            this.startWidth = this.targetElement.offsetWidth;

            this.widthChangeStart.emit();

            this.dragSub.unsubscribe();
            this.dragSub = new Subscription();

            this.dragSub.add(
                mousemove$.pipe(takeUntil(mouseup$)).subscribe((moveEvent) => {
                    this.pendingEvent = moveEvent;
                    if (this.rAF) return;
                    this.rAF = requestAnimationFrame(() => {
                        this.rAF = 0;
                        const ev = this.pendingEvent!;
                        this.pendingEvent = null;
                        this.handleMove(ev);
                    });
                })
            );

            this.dragSub.add(
                mouseup$.subscribe(() => {
                    this.cancelRaf();
                    this.dragSub.unsubscribe();
                    this.widthChangeEnd.emit();
                })
            );
        });
    }

    private handleMove(event: MouseEvent): void {
        const dx = event.clientX - this.startX;
        const dy = event.clientY - this.startY;
        const theta = (this.rotation * Math.PI) / 180;
        const localDeltaX = dx * Math.cos(theta) + dy * Math.sin(theta);
        const newWidth = Math.round(
            Math.max(
                this.minWidth,
                this.direction === 'right' ? this.startWidth + localDeltaX : this.startWidth - localDeltaX
            )
        );

        this.widthChange.emit(newWidth);
    }

    private cancelRaf(): void {
        if (this.rAF) {
            cancelAnimationFrame(this.rAF);
            this.rAF = 0;
            this.pendingEvent = null;
        }
    }

    public ngOnDestroy(): void {
        this.cancelRaf();
        this.startSub?.unsubscribe();
        this.dragSub.unsubscribe();
    }
}
